import {Injectable} from '@angular/core';
import {AbstractControl, UntypedFormArray, ValidationErrors} from '@angular/forms';
import {CountryBpTaxPayload, TaxIdValidationPayload} from '../../../model/dcp-config-service/tax-config';
import {CountrySelectorService} from '../../../services/country-selector.service';
import {DcpUserService} from '../../../services/user/dcp-user.service';
import {User} from '@spartacus/core';

@Injectable({providedIn: 'root'})
export class TaxInformationValidationService {
  constructor(
    private countrySelectorService: CountrySelectorService,
    private dcpUserService: DcpUserService
  ) {
  }

  /**
   * Validate amount of attached files, pass amount limit
   * and receive validation error when files are more than amount defined
   *
   * @param amount
   * @returns ValidatorFn
   */
  public createFileAmountValidator(amount: number) {
    return (formArray: UntypedFormArray): ValidationErrors | null =>
      formArray.controls.length > amount ? {amount: true} : null;
  }

  /**
   * Validate attached files total size
   *
   * @param sizeLimit total allowed size in bytes
   * @returns ValidatorFn
   */
  public createFileTotalSizeValidator(sizeLimit: number) {
    return (formArray: UntypedFormArray): ValidationErrors | null => {
      let totalSize = 0;
      for (const control of formArray.controls) {
        totalSize += (control.value as File).size;
      }
      return totalSize > sizeLimit ? {totalSize: true} : null;
    };
  }

  /**
   * Validate single file for file size
   *
   * @param sizeLimit allowed file size in bytes
   * @returns ValidatorFn
   */
  public createFileSizeValidator(sizeLimit: number) {
    return (control: AbstractControl): ValidationErrors | null =>
      (control.value as File).size > sizeLimit ? {size: true} : null;
  }

  /**
   * Validate single file to match with allowed file type
   *
   * @param typeWhitelist string array with allowed filetypes
   * @returns ValidatorFn
   */
  public createFileTypeValidator(typeWhitelist: string[]) {
    return (control: AbstractControl): ValidationErrors | null => {
      const nameParts = (control.value as File)?.name?.split('.') || [];
      if (nameParts.length < 2) {
        return {fileType: true};
      }
      const type = nameParts[nameParts.length - 1];
      return typeWhitelist.indexOf(type.toLocaleLowerCase()) === -1 ? {fileType: true} : null;
    };
  }

  public mapCountryBpTaxPayload(user: User): CountryBpTaxPayload {
    const countryIsoCode = this.countrySelectorService.getCurrentCountry().isocode;
    const businessPartnerType = this.dcpUserService.getUserCompanyType(user);
    return {
      countryIsoCode,
      businessPartnerType,
    };
  }

  /**
   * Getting current value of tax-id-validation related fields as a payload.
   */
  public getCurrentTaxValidationPayload(taxType: string, taxNumber: string, user: User): TaxIdValidationPayload {
    return {
      taxType,
      taxNumber,
      country: this.countrySelectorService.getCurrentCountry().isocode,
      individual: this.dcpUserService.isUserCompanyTypeIndividual(user)
    };
  }
}
